import { Component, Input, Output, EventEmitter, SecurityContext, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { SharedPopupService } from '../../services/shared-popup.service';
import { LandingService } from 'src/app/core/services/landing.service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.css'],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateX(0)' }))
      ]),
      transition(':leave', [
        animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translateX(100%)' }))
      ])
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class NotificationPanelComponent implements OnInit, OnChanges {
  // Input to control panel open/close state from parent
  @Input() isOpen: boolean = false;

  // Outputs to notify parent component
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() notificationCountChange = new EventEmitter<number>();
  
  // Internal state
  activeTab: string = 'triggered';
  notifications: any[] = [];
  totalAlerts: number = 0;
  isLoading: boolean = false;

  showClearAlertConfirmation: boolean = false;
  clearAlertsMessage: string = "";
  deleteAlertSuccessMessage: string = "";

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
    private sharedPopupService: SharedPopupService,
    private landingService: LandingService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    // Initial load if panel is open
    if (this.isOpen) {
      this.loadNotifications();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When panel opens, load notifications
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      this.loadNotifications();
    }
  }

  /**
   * Load notifications from API
   */
  loadNotifications(): void {
    this.isLoading = true;
    
    const client = this.landingService.getSelectedClient();
    const study = this.landingService.getSelectedStudy();
    const userEmail = localStorage.getItem('useremail') ?? '';

    this.sharedPopupService.getTriggeredAlertList(userEmail, client, study).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        
        try {
          const parsedData = JSON.parse(response.Data);
          if (parsedData.TriggeredAlerts && 
              parsedData.TriggeredAlerts.items && 
              Array.isArray(parsedData.TriggeredAlerts.items)) {
            
            // Map all alerts
            this.notifications = parsedData.TriggeredAlerts.items.map((alert: any) => ({
              header: alert.AlertName,
              alertId: alert.AlertId,
              text: alert.AlertDescription,
              sharedBy: alert.SharedBy || null,
            }));
            this.totalAlerts = parsedData.TriggeredAlerts.totalCount || 0;
            
            // Emit count to parent
            this.notificationCountChange.emit(this.totalAlerts);
          } else {
            this.notifications = [];
            this.totalAlerts = 0;
            this.notificationCountChange.emit(0);
          }
        } catch (error) {
          console.error('Error parsing notification data:', error);
          this.notifications = [];
          this.totalAlerts = 0;
          this.notificationCountChange.emit(0);
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error('Error loading notifications:', error);
        this.notifications = [];
        this.totalAlerts = 0;
        this.notificationCountChange.emit(0);
      }
    });
  }

  /**
   * Sanitize HTML content
   */
  getSanitizedHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Strip HTML tags from text
   */
  stripHtml(text: string): string {
    return text.replace(/<[^>]*>/g, '');
  }

  /**
   * Switch between tabs
   */
  switchTab(tab: string): void {
    if (tab === 'triggered') {
      this.activeTab = tab;
    }
    // Other tabs are disabled for now
  }

  /**
   * Clear all alerts (or first 20 if more than 20)
   */
  onClearAll(): void {
    this.clearAlertsMessage = this.totalAlerts <= 20 
      ? 'Are you sure you want to clear all triggered alerts?'
      : 'Are you sure you want to clear the first 20 triggered alerts?';
    this.showClearAlertConfirmation = true;
  }

  onClearConfirmation(clear: any) {
    this.showClearAlertConfirmation = false;

    const alertIds = this.notifications.map(notification => notification.alertId);
    
    if (clear == 1) {
      this.deleteAlerts(alertIds, this.totalAlerts);
    }
  }

  /**
   * Delete a single alert
   */
  deleteAlert(notification: any, index: number): void {
    const alertIds = [notification.alertId];
    this.deleteAlerts(alertIds, 1, notification.header);
  }

  /**
   * Delete alerts via API
   */
  private deleteAlerts(alertIds: string[], count: number, alertName?: string): void {
    this.sharedPopupService.updateTriggeredAlertList(alertIds).subscribe({
      next: () => {
        // Show success message
        if (alertName) {
          this.deleteAlertSuccessMessage = `Deleted ${alertName} successfully.`;
        } else if (count <= 20) {
          this.deleteAlertSuccessMessage = 'Deleted all alerts successfully.';
        } else {
          this.deleteAlertSuccessMessage = 'Deleted first 20 alerts successfully.';
        }
        
        // // hide message after 2s 
        // setTimeout(() => {
        //   this.deleteAlertSuccessMessage = "";
        // }, 2000);

        this.toastService.show(this.deleteAlertSuccessMessage);

        // Reload notifications to get updated list
        this.loadNotifications();
      },
      error: (error: any) => {
        console.error('Error deleting alerts:', error);
        alert('Failed to delete alert(s). Please try again.');
      }
    });
  }

  /**
   * Close the panel
   */
  closePanel(): void {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  /**
   * Navigate to Alert Management module
   */
  navigateToAlertManagement(): void {
    const data = {
      client_code: this.landingService.getSelectedClient(),
      study: this.landingService.getSelectedStudy(),
      studyList: [],
      navigateToTriggered: true
    };
    
    this.closePanel();
    this.router.navigateByUrl('alert-management', { state: data });
  }
}
